import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';

import type { IInvitation } from '@/domain/entities/invitation';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ErrorNotice } from '@/components/ui/error-notice';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { OrganizationInviteMemberDialog, type InviteMemberResult } from './OrganizationInviteMemberDialog';

type OrganizationInvitationsTabProps = {
  invitations: IInvitation[];
  organizationName?: string;
  isFetching: boolean;
  isMutating: boolean;
  hasFetched: boolean;
  error: string | null;
  canManageInvitations: boolean;
  onRetry: () => void;
  onInviteMember?: (email: string) => Promise<InviteMemberResult>;
  onResend: (invitation: IInvitation) => Promise<void>;
  onRevoke: (invitation: IInvitation) => Promise<void>;
  onDelete: (invitation: IInvitation) => Promise<void>;
  formatDate: (timestamp?: number) => string;
};

function getStatusBadge(invitation: IInvitation) {
  if (invitation.status === 'pending') {
    return <Badge variant="default">Pending</Badge>;
  }

  if (invitation.status === 'accepted') {
    return <Badge className="bg-green-600">Accepted</Badge>;
  }

  if (invitation.status === 'expired') {
    return (
      <Badge className="border-amber-400/70 bg-amber-400/25 text-amber-200 shadow-[0_0_0_1px_rgba(251,191,36,0.18)]">
        <AlertTriangle className="h-3 w-3" />
        Expired
      </Badge>
    );
  }

  return <Badge variant="outline">Revoked</Badge>;
}

function getDeliveryBadge(deliveryStatus?: IInvitation['deliveryStatus']) {
  if (deliveryStatus === 'sent') {
    return <Badge className="bg-green-600">Sent</Badge>;
  }

  if (deliveryStatus === 'failed') {
    return <Badge variant="destructive">Failed</Badge>;
  }

  return <Badge variant="outline">Pending</Badge>;
}

export function OrganizationInvitationsTab({
  invitations,
  organizationName,
  isFetching,
  isMutating,
  hasFetched,
  error,
  canManageInvitations,
  onRetry,
  onInviteMember,
  onResend,
  onRevoke,
  onDelete,
  formatDate,
}: OrganizationInvitationsTabProps) {
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [resendingInvitationId, setResendingInvitationId] = useState<string | null>(null);
  const [resendDialogInvitationId, setResendDialogInvitationId] = useState<string | null>(null);
  const [resendDialogError, setResendDialogError] = useState<string | null>(null);
  const [deletingInvitationId, setDeletingInvitationId] = useState<string | null>(null);
  const [deleteDialogInvitationId, setDeleteDialogInvitationId] = useState<string | null>(null);
  const [deleteDialogError, setDeleteDialogError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleResend = async (invitation: IInvitation) => {
    setActionError(null);
    setResendDialogError(null);
    setResendingInvitationId(invitation.id);

    try {
      await onResend(invitation);
      setResendDialogInvitationId(null);
    } catch (err) {
      const error = err as { message?: string };
      setResendDialogError(error?.message || 'Failed to resend invitation.');
    } finally {
      setResendingInvitationId(null);
    }
  };

  const handleRevoke = async (invitation: IInvitation) => {
    setActionError(null);

    try {
      await onRevoke(invitation);
    } catch (err) {
      const error = err as { message?: string };
      setActionError(error?.message || 'Failed to revoke invitation.');
    }
  };

  const handleDelete = async (invitation: IInvitation) => {
    setActionError(null);
    setDeleteDialogError(null);
    setDeletingInvitationId(invitation.id);

    try {
      await onDelete(invitation);
      setDeleteDialogInvitationId(null);
    } catch (err) {
      const error = err as { message?: string };
      setDeleteDialogError(error?.message || 'Failed to delete invitation.');
    } finally {
      setDeletingInvitationId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <CardTitle>Invitations</CardTitle>
          <CardDescription>Review pending, accepted, expired, and revoked organization invitations.</CardDescription>
        </div>

        <div className="flex flex-wrap gap-2">
          {canManageInvitations && onInviteMember ? (
            <Button type="button" onClick={() => setIsInviteModalOpen(true)} disabled={isMutating}>
              Invite Member
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={onRetry} disabled={isFetching}>
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <ErrorNotice message={actionError} title="Invitation Action Failed" />

        {!hasFetched || isFetching ? (
          <div className="text-muted-foreground flex min-h-[200px] flex-col items-center justify-center gap-2 text-sm">
            <LoadingSpinner size={40} />
            <p>Loading invitations...</p>
          </div>
        ) : error ? (
          <div className="space-y-3">
            <ErrorNotice message={error} title="Failed to Load Invitations" />
            <Button type="button" variant="outline" onClick={onRetry}>
              Retry
            </Button>
          </div>
        ) : invitations.length === 0 ? (
          <p className="text-muted-foreground text-sm">No invitations found for this organization.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Delivery</TableHead>
                <TableHead>Inviter</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invitations.map((invitation) => {
                const canResend =
                  canManageInvitations && (invitation.status === 'pending' || invitation.status === 'expired');
                const canRevoke = canManageInvitations && invitation.status === 'pending';
                const canDelete =
                  canManageInvitations && (invitation.status === 'revoked' || invitation.status === 'expired');
                const isExpired = invitation.status === 'expired';
                const isResending = resendingInvitationId === invitation.id;
                const isDeleting = deletingInvitationId === invitation.id;

                return (
                  <TableRow
                    key={invitation.id}
                    className={isExpired ? 'bg-amber-400/10 hover:bg-amber-400/15' : undefined}
                  >
                    <TableCell>
                      <div className="space-y-1">
                        <p className={isExpired ? 'font-medium text-amber-200' : 'font-medium'}>{invitation.email}</p>
                        <p className="text-muted-foreground text-xs">{invitation.id}</p>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(invitation)}</TableCell>
                    <TableCell>{getDeliveryBadge(invitation.deliveryStatus)}</TableCell>
                    <TableCell>{invitation.inviterName || invitation.invitedBy || 'Unknown'}</TableCell>
                    <TableCell className={isExpired ? 'font-medium text-amber-200' : undefined}>
                      <div className="flex items-center gap-1.5">
                        {isExpired ? <AlertTriangle className="h-4 w-4" /> : null}
                        {formatDate(invitation.expiresAt)}
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(invitation.createdAt)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <AlertDialog
                          open={resendDialogInvitationId === invitation.id}
                          onOpenChange={(open) => {
                            if (isResending) return;
                            setResendDialogError(null);
                            setResendDialogInvitationId(open ? invitation.id : null);
                          }}
                        >
                          <AlertDialogTrigger asChild>
                            <Button type="button" variant="outline" size="sm" disabled={!canResend || isMutating}>
                              Resend
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Resend invitation?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This sends a new invitation email to {invitation.email} and refreshes the invitation
                                expiry.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <ErrorNotice message={resendDialogError} title="Resend Failed" />
                            <AlertDialogFooter>
                              <AlertDialogCancel disabled={isResending}>Cancel</AlertDialogCancel>
                              <Button
                                type="button"
                                onClick={() => void handleResend(invitation)}
                                disabled={isResending}
                              >
                                {isResending ? 'Resending...' : 'Resend Invitation'}
                              </Button>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>

                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button type="button" variant="outline" size="sm" disabled={!canRevoke || isMutating}>
                              Revoke
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Revoke invitation?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This revokes the pending invitation for {invitation.email}. This action cannot be
                                undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => void handleRevoke(invitation)}>
                                Revoke Invitation
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>

                        <AlertDialog
                          open={deleteDialogInvitationId === invitation.id}
                          onOpenChange={(open) => {
                            if (isDeleting) return;
                            setDeleteDialogError(null);
                            setDeleteDialogInvitationId(open ? invitation.id : null);
                          }}
                        >
                          <AlertDialogTrigger asChild>
                            <Button type="button" variant="outline" size="sm" disabled={!canDelete || isMutating}>
                              Delete
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete invitation?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This permanently deletes the {invitation.status} invitation for {invitation.email}.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <ErrorNotice message={deleteDialogError} title="Delete Failed" />
                            <AlertDialogFooter>
                              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                              <Button type="button" onClick={() => void handleDelete(invitation)} disabled={isDeleting}>
                                {isDeleting ? 'Deleting...' : 'Delete Invitation'}
                              </Button>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {onInviteMember ? (
          <OrganizationInviteMemberDialog
            open={isInviteModalOpen}
            organizationName={organizationName}
            isSubmitting={isMutating}
            onOpenChange={setIsInviteModalOpen}
            onInviteMember={onInviteMember}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}
